import * as React from "react";
import { useEffect, useState } from "react";
import { ChatBubbleLeftRightIcon, PlusIcon } from "@heroicons/react/24/solid";

import { ContentSectionAlt } from "../Layout/ContentSectionAlt";
import { api } from "../api";
import { ForumTopicResponse } from "../../../server/routes/forums";
import { RLink } from "../DesignSystem/RLink";
import { LinkToUser } from "../DesignSystem/LinkToUser";
import { FormattedText } from "../DesignSystem/FormattedText";
import {
  FeedElementContainer,
  ActionPanel,
} from "../DesignSystem/formatting/FeedComponents";

const DEFAULT_BOARD = "chips";

const BOARDS = [
  { id: "chips", label: "Chips" },
  { id: "memes", label: "Memes" },
] as const;

export const ForumPage = (): JSX.Element => {
  const [topics, setTopics] = useState<ForumTopicResponse[]>([]);
  const [currentBoard, setCurrentBoard] = useState<string>(DEFAULT_BOARD);

  useEffect(() => {
    let cancelled = false;

    const fetchTopics = async () => {
      const result = await api.get("/api/forum");

      if (!cancelled && result.data) {
        setTopics(result.data);
      }
    };

    fetchTopics();

    return () => {
      cancelled = true;
    };
  }, []);

  const filteredTopics = topics.filter(
    (topic) => topic.topic.board === currentBoard
  );

  return (
    <ContentSectionAlt>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ChatBubbleLeftRightIcon className="h-8 w-8 text-blue-500" />
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
            Forum
          </h1>
        </div>

        <RLink to="/forum/new-topic">
          <button className="flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-600">
            <PlusIcon className="h-5 w-5" />
            New Topic
          </button>
        </RLink>
      </div>

      {/* Board tabs */}
      <div className="mb-6 flex gap-2">
        {BOARDS.map((board) => (
          <button
            key={board.id}
            onClick={() => setCurrentBoard(board.id)}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              currentBoard === board.id
                ? "bg-blue-500 text-white"
                : "bg-white text-slate-600 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            }`}
          >
            {board.label}
          </button>
        ))}
      </div>

      {/* Topics list */}
      <div>
        {filteredTopics.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center dark:border-white/10 dark:bg-slate-800">
            <p className="text-slate-500 dark:text-slate-400">
              No topics in this board yet. Be the first to start a discussion!
            </p>
          </div>
        ) : (
          filteredTopics.map(
            ({ topic, user: topicUser, comments, author_banned }) => {
              const lastComment =
                comments.length > 0 ? comments[comments.length - 1] : null;

              return (
                <FeedElementContainer
                  key={topic.id}
                  href={`/forum/topic/${topic.id}`}
                  icon="comment"
                  avatars={[
                    {
                      username: topicUser.username,
                      avatar_url: topicUser.avatar_url,
                    },
                  ]}
                  title={
                    <RLink white to={`/forum/topic/${topic.id}`}>
                      {topic.title}
                    </RLink>
                  }
                  subtitle={
                    <span>
                      by <LinkToUser linkUser={topicUser} /> &middot;{" "}
                      {new Date(topic.created).toDateString()} &middot;{" "}
                      {comments.length}{" "}
                      {comments.length === 1 ? "comment" : "comments"}
                    </span>
                  }
                  bottom={
                    <ActionPanel reply={{ url: `/forum/topic/${topic.id}` }} />
                  }
                >
                  {author_banned && (
                    <div className="mb-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
                      The author of this post is banned. Only moderators can see
                      this.
                    </div>
                  )}
                  {topic.body && (
                    <div className="mb-2 text-sm text-slate-600 dark:text-slate-300">
                      <FormattedText text={topic.body} maxLength={200} />
                    </div>
                  )}
                  {lastComment && (
                    <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-700/50">
                      <div className="mb-1 text-xs text-slate-500 dark:text-slate-400">
                        Latest from <LinkToUser linkUser={lastComment.user} />
                      </div>
                      <div className="text-sm">
                        <FormattedText
                          text={lastComment.comment.content}
                          maxLength={200}
                        />
                      </div>
                    </div>
                  )}
                </FeedElementContainer>
              );
            }
          )
        )}
      </div>
    </ContentSectionAlt>
  );
};
